import {
  Button,
  Cell,
  Column,
  MenuTrigger,
  Row,
  Table,
  TableBody,
  TableFooter,
  TableHeader,
} from '@eleveo/components-react';
import { BinIcon, PencilIcon, ThreeDotsIcon, UserAddIcon } from '@eleveo/icons-react';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { DateFormatter, getLocalTimeZone, parseDate, parseTime } from '@internationalized/date';
import { uniq } from 'ramda';
import React from 'react';
// eslint-disable-next-line import-x/no-extraneous-dependencies
import { useDateFormatter } from 'react-aria';
import { useTranslation } from 'react-i18next';

import { LIST_CONTAINER_TAG, getListItemTag } from './constants';
import { Permissions, useAuth } from '../../app/AuthorizationProvider';
import { DayTemplate, ShiftStateType, ShiftTemplate, TimeRange } from '../../graphql/components';
import { toDateFromTime } from '../../utils/date';

import type { TFunction } from 'i18next';

export const renderTimeValue = (
  dayTemplates: DayTemplate[],
  t: TFunction,
  rangeField: keyof Omit<TimeRange, '__typename'>,
  locale: string
) => {
  if (dayTemplates.length === 0) {
    return '';
  }

  const timeFormatter = new DateFormatter(locale, {
    timeStyle: 'short',
  });

  if (dayTemplates.length === 1) {
    return timeFormatter.format(
      toDateFromTime(parseTime(dayTemplates[0].shiftRange[rangeField])).toDate(getLocalTimeZone())
    );
  }

  const uniqTimesValues = uniq(dayTemplates.map(({ shiftRange }) => shiftRange[rangeField]));

  if (uniqTimesValues.length > 1) {
    return t('shiftTemplate.various');
  }

  return timeFormatter.format(toDateFromTime(parseTime(uniqTimesValues[0])).toDate(getLocalTimeZone()));
};

// Based on the assumption that odd/even and specific days will include only those days in the field that have defined shifts
export const renderBreaks = (dayTemplates: DayTemplate[], t: TFunction) => {
  const allBreaks = uniq(dayTemplates.flatMap(({ breaks }) => breaks.length));

  if (allBreaks.length === 1) {
    return allBreaks[0] === 0 ? t('shiftTemplate.noBreaks') : allBreaks[0];
  }

  return t('shiftTemplate.various');
};

type ShiftTemplatesTableProps = {
  activeId?: string;
  children: React.ReactNode;
  onAssign: (shiftTemplate: ShiftTemplate) => void;
  onEdit: (shiftTemplate: ShiftTemplate) => void;
  onDelete: (shiftTemplate: ShiftTemplate) => void;
  shiftTemplates: ShiftTemplate[];
};

export const ShiftTemplatesTable = ({
  activeId,
  children,
  onAssign,
  onEdit,
  onDelete,
  shiftTemplates,
}: ShiftTemplatesTableProps) => {
  const { userHasPermissions, locale, timezone } = useAuth();
  const { t } = useTranslation();

  const formatter = useDateFormatter({ dateStyle: 'medium', timeZone: timezone });

  const hasEditPermission = userHasPermissions([Permissions.EditShiftTemplate]);
  const hasDeletePermission = userHasPermissions([Permissions.DeleteShiftTemplate]);
  const hasPermissions = hasEditPermission || hasDeletePermission;

  const columns = [
    {
      key: 'name',
      name: t('shiftTemplate.shiftName'),
    },
    {
      key: 'type',
      name: t('shiftTemplate.shiftType'),
    },
    {
      key: 'effectiveDate',
      name: t('shiftTemplate.effectiveDate'),
    },
    {
      key: 'start',
      name: t('shiftTemplate.startTime'),
    },
    {
      key: 'end',
      name: t('shiftTemplate.endTime'),
    },
    {
      key: 'breaks',
      name: t('shiftTemplate.breaks'),
    },
    ...(hasPermissions
      ? [{ key: 'actions', name: t('layout.action.actions'), isActions: true, hideHeader: true }]
      : []),
  ];

  const rows = shiftTemplates.map((shiftTemplate) => ({
    id: shiftTemplate.id,
    name: <strong>{shiftTemplate.name}</strong>,
    effectiveDate: formatter.format(parseDate(shiftTemplate.effectiveDate).toDate(timezone)),
    type: <strong>{t(`shiftTemplate.type.${shiftTemplate.type}`)}</strong>,
    start: renderTimeValue(shiftTemplate.dayTemplates, t, 'startTime', locale),
    end: renderTimeValue(shiftTemplate.dayTemplates, t, 'endTime', locale),
    breaks: renderBreaks(shiftTemplate.dayTemplates, t),
    actions: (
      <MenuTrigger
        items={[
          ...(hasEditPermission
            ? [
                {
                  id: '1',
                  icon: <UserAddIcon />,
                  onClick: () => onAssign(shiftTemplate),
                  testId: getListItemTag('assign'),
                  text: t('person.assignPeople'),
                },
                {
                  id: '2',
                  icon: <PencilIcon />,
                  onClick: () => onEdit(shiftTemplate),
                  testId: getListItemTag('edit'),
                  text: t('layout.action.edit'),
                },
              ]
            : []),
          ...(hasDeletePermission
            ? [
                {
                  id: '3',
                  icon: <BinIcon />,
                  onClick: () => onDelete(shiftTemplate),
                  testId: getListItemTag('delete'),
                  text: t('layout.action.delete'),
                  // TODO: Solve how to comunicate the reason of disabled menuitem
                  // shiftTemplate.state === ShiftStateType.Assigned
                  //   ? 'response.fe.autoscheduler.template.isUsed'
                  //   : undefined
                  isDisabled: shiftTemplate.state === ShiftStateType.Assigned,
                },
              ]
            : []),
        ]}
      >
        <Button aria-label={t('layout.action.actions')} isSmall testId={getListItemTag('actions')} variant="quiet">
          <ThreeDotsIcon />
        </Button>
      </MenuTrigger>
    ),
  }));

  return (
    <Table
      aria-label={t('shiftTemplate.shiftTemplate', { count: 2 })}
      isStickyHeader
      highlightedKey={activeId}
      testId={LIST_CONTAINER_TAG}
    >
      <TableHeader columns={columns}>
        {({ key, name, ...columnProps }) => (
          <Column key={key} {...columnProps}>
            {name}
          </Column>
        )}
      </TableHeader>
      <TableBody items={rows}>{(item) => <Row>{(columnKey) => <Cell>{item[columnKey]}</Cell>}</Row>}</TableBody>
      <TableFooter>{children}</TableFooter>
    </Table>
  );
};
